import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Link as ChakraLink,
  Heading,
  Stack,
  Text,
  usePrevious,
} from '@chakra-ui/react';
import { useBreakpointValue } from '@chakra-ui/media-query';
import { useRouter } from 'next/router';

import { axios } from 'src/lib/axios';
import useAuth from 'src/app/auth/use-auth';
import useInterval from 'src/hooks/use-interval';
import { signIn } from 'src/app/auth/service';
import { SvgEmailSent } from 'src/components/icons';
import { useBroadcastVerify } from '../use-broadcast-verify';

interface VerificationProps {
  userInfo: {
    username: string;
    email: string;
    password: string;
  };
  verifyToken: string;
}

function Verification({ userInfo, verifyToken }: VerificationProps) {
  const [timer, setTimer] = useState(60);
  const tokenRef = useRef(verifyToken);
  const [verified] = useBroadcastVerify();
  const previousVerified = usePrevious(verified);

  const headingFontSize = useBreakpointValue({ base: 'xl', md: '2xl' });
  const router = useRouter();
  const { refetch } = useAuth();

  const resetTick = async () => {
    const response = await axios.get(
      `/resend_auth?email=${encodeURIComponent(userInfo.email)}`
    );

    if (response?.data?.data.token) {
      tokenRef.current = response.data.data.token;
      setTimer(60);
    }
  };

  const intervalCallback = () => {
    if (timer > 0) {
      setTimer(timer - 1);
    }
  };

  useInterval({ callback: intervalCallback });

  useEffect(() => {
    if (previousVerified !== verified && verified === 'ok') {
      signIn(userInfo).then((res) => {
        if (res.code === 1000) {
          refetch().then(() => {
            router.push('/');
          });
        }
      });
    }
  }, [verified, previousVerified, refetch, router, userInfo]);

  return (
    <Box>
      <Box color="green.500" fontSize="64px">
        <SvgEmailSent />
      </Box>

      <Stack spacing={[3, 2, 4]} mt={3}>
        <Heading size={headingFontSize}>Verification Email Sent!</Heading>

        <Text>
          We have sent a verification email to{' '}
          <ChakraLink>{userInfo.email}</ChakraLink>. Please check the email for
          further Instructions.
        </Text>

        <Button
          rounded="ellipse"
          width="250px"
          isDisabled={timer > 0}
          onClick={resetTick}
        >
          {timer === 0 ? 'Resend Email' : `Resend Email in ${timer} sec.`}
        </Button>
      </Stack>
    </Box>
  );
}

export default Verification;
